import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Incident, ResponseOption } from '../types/Incident';
import { announce, createKeyboardNavigation } from '../utils/a11y';
import { useThemeUtils } from '../hooks/useThemeUtils';
import { TIME_CONSTANTS } from '../constants';
import './IncidentScreen.css';

interface IncidentScreenProps {
  incident: Incident;
  onResponse: (optionId: string, responseTime: number) => void;
  onTimeOut: () => void;
  onOptionHover?: (optionId: string) => void;
  onOptionLeave?: () => void;
  highlightedOptionId?: string | null;
}

const IncidentScreen: React.FC<IncidentScreenProps> = ({ 
  incident, 
  onResponse, 
  onTimeOut,
  onOptionHover,
  onOptionLeave,
  highlightedOptionId 
}) => {
  const { themeClass, riskColors } = useThemeUtils();
  const [timeRemaining, setTimeRemaining] = useState(incident.timeLimit);
  const [startTime] = useState(Date.now());
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [hasResponded, setHasResponded] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const optionRefs = useRef<HTMLButtonElement[]>([]);

  // Reset state when incident changes
  useEffect(() => {
    setSelectedOption(null);
    setHasResponded(false);
    setTimeRemaining(incident.timeLimit);
  }, [incident.id, incident.timeLimit]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          announce('Time limit reached. Moving to next incident.', 'assertive');
          onTimeOut();
          return 0;
        }
        // Announce time warnings
        if (prev === TIME_CONSTANTS.TIMER_WARNING_30_SECONDS) {
          announce('30 seconds remaining', 'polite');
        } else if (prev === TIME_CONSTANTS.TIMER_WARNING_10_SECONDS) {
          announce('10 seconds remaining', 'assertive');
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onTimeOut]);

  const handleOptionSelect = useCallback((option: ResponseOption, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (hasResponded) return; // Prevent multiple selections for this incident
    
    setSelectedOption(option.id);
    announce(`Selected: ${option.label}. Click Confirm Response to submit.`, 'polite');
  }, [hasResponded]);

  // Focus management and keyboard navigation
  useEffect(() => {
    // Focus on title when incident changes
    if (titleRef.current) {
      titleRef.current.focus();
    }

    // Set up keyboard navigation for options
    const navigation = createKeyboardNavigation(
      optionRefs.current.filter(Boolean),
      {
        orientation: 'vertical',
        loop: true,
        onSelect: (element, index) => {
          const option = incident.responseOptions[index];
          if (option && !hasResponded) {
            handleOptionSelect(option);
          }
        }
      }
    );

    navigation.activate();

    return () => {
      navigation.deactivate();
    };
  }, [incident, hasResponded, handleOptionSelect]);

  const handleConfirmResponse = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!selectedOption || hasResponded) return;
    
    setHasResponded(true);
    announce(`Response confirmed: ${selectedOption}`, 'polite');
    
    const responseTime = Math.floor((Date.now() - startTime) / 1000);
    setTimeout(() => {
      onResponse(selectedOption, responseTime);
    }, 300); // Small delay for visual feedback
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = (): string => {
    const percentage = (timeRemaining / incident.timeLimit) * 100;
    if (percentage > 50) return themeClass({ light: 'text-emerald-600', dark: 'text-emerald-400' });
    if (percentage > 25) return themeClass({ light: 'text-amber-600', dark: 'text-amber-400' });
    return themeClass({ light: 'text-red-600', dark: 'text-red-400' });
  };

  const getSeverityClasses = (severity: string): string => {
    switch (severity) {
      case 'critical': return riskColors.critical.background + ' ' + riskColors.critical.text;
      case 'high': return riskColors.high.background + ' ' + riskColors.high.text;
      case 'medium': return riskColors.medium.background + ' ' + riskColors.medium.text;
      case 'low': return riskColors.low.background + ' ' + riskColors.low.text;
      default: return themeClass({ light: 'bg-gray-100 text-gray-700', dark: 'bg-gray-800 text-gray-300' });
    }
  };

  return (
    <div className="incident-screen" data-testid="incident-screen" role="region" aria-label="Incident response">
      {/* Sticky title bar */}
      <div className="sticky-incident-bar" role="banner" aria-label="Incident status">
        <span className="sticky-title">{incident.title}</span>
        <div className={`sticky-timer ${getTimerClass()}`} aria-label={`Time remaining: ${formatTime(timeRemaining)}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
          </svg>
          <span>{formatTime(timeRemaining)}</span>
        </div>
      </div>

      <div className="incident-content">
        <div className="incident-header">
          <div className={`severity-badge ${getSeverityClasses(incident.severity)}`} role="status" aria-label={`Severity: ${incident.severity}`}>
            {incident.severity.toUpperCase()}
          </div>
          <h1 ref={titleRef} tabIndex={-1}>{incident.title}</h1>
        </div>

        <div className="incident-description" role="article">
          <p id="incident-desc">{incident.description}</p>
        </div>

        <div className="response-section" role="region" aria-label="Response options">
          <h3 id="response-heading">Select Your Response</h3>
          <div className="response-grid" role="group" aria-labelledby="response-heading" aria-describedby="incident-desc">
            {incident.responseOptions.map((option, index) => (
              <button
                key={option.id}
                ref={el => {
                  if (el) optionRefs.current[index] = el;
                }}
                className={`response-option ${selectedOption === option.id ? 'selected' : ''} ${highlightedOptionId === option.id ? 'highlighted' : ''}`}
                onClick={(e) => handleOptionSelect(option, e)}
                onMouseEnter={() => onOptionHover && onOptionHover(option.id)}
                onMouseLeave={() => onOptionLeave && onOptionLeave()}
                disabled={hasResponded}
                aria-pressed={selectedOption === option.id}
                aria-label={`${option.label}. ${option.description}`}
              >
                <h4>{option.label}</h4>
                <p>{option.description}</p>
              </button>
            ))}
          </div>
          
          {/* Confirmation Section */}
          {selectedOption && !hasResponded && (
            <div className="confirmation-section" role="region" aria-label="Confirm response">
              <button
                className="confirm-response-button"
                onClick={handleConfirmResponse}
                aria-label={`Confirm your response: ${incident.responseOptions.find(opt => opt.id === selectedOption)?.label}`}
              >
                Confirm Response
              </button>
              <p className="selection-info">
                Selected: {incident.responseOptions.find(opt => opt.id === selectedOption)?.label}
              </p>
            </div>
          )}
        </div>

        <div className="impact-warnings" role="region" aria-label="Potential impact assessment">
          <h3 id="impact-heading">Potential Impact</h3>
          <div className="impact-grid" role="list" aria-labelledby="impact-heading">
            <div className="impact-item" role="listitem">
              <strong>Security:</strong> <span aria-label={`Security impact: ${incident.potentialImpact.security}`}>{incident.potentialImpact.security}</span>
            </div>
            <div className="impact-item" role="listitem">
              <strong>Reputation:</strong> <span aria-label={`Reputation impact: ${incident.potentialImpact.reputation}`}>{incident.potentialImpact.reputation}</span>
            </div>
            <div className="impact-item" role="listitem">
              <strong>Financial:</strong> <span aria-label={`Financial impact: ${incident.potentialImpact.financial}`}>{incident.potentialImpact.financial}</span>
            </div>
            <div className="impact-item" role="listitem">
              <strong>Operational:</strong> <span aria-label={`Operational impact: ${incident.potentialImpact.operational}`}>{incident.potentialImpact.operational}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentScreen;